/* eslint-disable dot-notation */
import { ChangeDetectionStrategy, Component, effect, inject, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe, CommonModule } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { Observable } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { LoginComponent } from './login/login.component';
import { select, Store } from '@ngxs/store';
import { AppSelectors } from './app.state';
import { CheckTokenAction, LogoutAction } from './core/authentication/auth.state.model';
import { SpinnerComponent } from './core/spinner/spinner/spinner.component';
import { WidgetMessageService } from './core/services/widget-message.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SidenavComponent } from './navigation/side-navigation/main-sidenav/sidenav-menu.component';
import { SpinnerService } from './core/spinner/service/spinner.service';
import { Title } from '@angular/platform-browser';
import { BreadcrumbComponent } from './navigation/breadcrumb/breadcrumb.component';
import { AppActions } from './app.actions';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { AboutComponent } from './core/components/about/about.component';
import { MatDialog } from '@angular/material/dialog';
import { AppSetupComponent } from './core/components/app-setup/app-setup.component';
import { LocalStorageService } from './core/services/local-storage.service';
import { InactivityService } from './core/services/inactivity.service';
import { AuthSyncService } from './core/authentication/auth-sync.service';

@Component({
  selector: 'pixl-root',
  imports: [
    CommonModule,
    MatToolbarModule,
    MatButtonModule,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    AsyncPipe,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    RouterModule,
    // ThemeToggleComponent,
    LoginComponent,
    SpinnerComponent,
    SidenavComponent,
    BreadcrumbComponent,
    MatSlideToggleModule,
    MatRadioModule,
    CommonModule,
    FormsModule,
    TranslocoModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
  private store = inject(Store);
  private breakpointObserver = inject(BreakpointObserver);
  private matIconReg = inject(MatIconRegistry);
  private titleService = inject(Title);
  private router = inject(Router);
  private translocoService = inject(TranslocoService);
  public localStorageService = inject(LocalStorageService);
  public inactivityService = inject(InactivityService);
  private authSyncService = inject(AuthSyncService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);

  public title = 'zav cms';
  public widgetService = inject(WidgetMessageService);
  public spinnerService = inject(SpinnerService);
  public authState = select(AppSelectors.authState);
  public fullMenu = signal(true);
  public activeLang = 'de';
  public availableLangs: string[];

  constructor() {
    this.titleService.setTitle('ZAV CMS');
    const savedLang = this.localStorageService.language();
    this.translocoService.setActiveLang(savedLang);
    this.activeLang = savedLang;
    this.availableLangs = this.translocoService.getAvailableLangs() as string[];

    effect(() => {
      if (this.authState().isAuthenticated) {
        this.inactivityService.start();
      }
      else {
        this.inactivityService.stop();
      }
    });

    // Use effect() instead of computed() if a side effect is outside of the signal scope
    effect(() => {
      if (this.widgetService.showMessage$()) {
        let panelClass = 'snackbar-success';
        switch (this.widgetService.widgetMessageType) {
          case 'mtError': panelClass = 'snackbar-error';
            break;
          case 'mtInfo': panelClass = 'snackbar-info';
            break;
          case 'mtWarning': panelClass = 'snackbar-warn';
            break;
        }

        this.snackBar.open(this.widgetService.widgetMessage, '', {
          horizontalPosition: 'end',
          verticalPosition: 'bottom',
          panelClass: [panelClass],
          duration: this.widgetService.widgetDuration
        });
      }
    });
  }

  private getNestedRouteTitles(): string[] {
    let currentRoute = this.router.routerState.root.firstChild;
    const titles: string[] = [];

    while (currentRoute) {
      if (currentRoute?.snapshot?.routeConfig?.data?.['title']) {
        titles.push((titles.length === 0 ? ' - ' : '') + currentRoute.snapshot.routeConfig.data['title']);
      }

      currentRoute = currentRoute.firstChild;
    }

    return titles;
  }

  ngOnInit(): void {
    const loggedIn = this.authState().isAuthenticated;
    if (!loggedIn) {
      this.store.dispatch(new CheckTokenAction());
    }
    this.matIconReg.setDefaultFontSetClass('material-symbols-outlined');

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.titleService.setTitle('ZAV CMS' + this.getNestedRouteTitles().join(' | '));
    });

    this.store.dispatch(new AppActions.RestoreAuthState());
    this.authSyncService.init();
  }

  public logout(): void {
    this.store.dispatch(new LogoutAction());
  }

  public about() {
    this.dialog.open(AboutComponent, {
      minWidth: '40vw',
    });
  }

  public isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
    .pipe(
      map(result => result.matches),
      shareReplay()
    );

  onLanguageChange(event: MatSlideToggleChange) {
    const isChecked = event.checked;
    if (isChecked) {
      this.translocoService.setActiveLang('en');
    }
    else {
      this.translocoService.setActiveLang('de');
    }
  }

  public getCurrentLanguage(): string {
    if (this.translocoService.getActiveLang() === 'de') {
      return 'Deutsch';
    }

    return 'English';
  }

  switchLang(lang: string): void {
    this.translocoService.setActiveLang(lang);
    this.activeLang = lang;
  }

  public formatCountdown(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  public setup(): void {
    this.dialog.open(AppSetupComponent, {
      minWidth: '300px',
      minHeight: '250px',
    });
  }
}
